import { FC, useCallback, useMemo } from 'react';
import { useViewTracking } from '~/utils/use-view-tracking';
import { Link } from '@remix-run/react';
import { useTranslation } from 'react-i18next';
import { CommentUtil, TComment } from 'shared/models';
import { useSemanticUrlBuilder } from '~/utils/use-semantic-url-builder';
import { useTimeAgoFormatter } from '~/utils/format';
import HeartIcon from 'icon/heart.svg';
import { CommentReplyList } from '@article/body/comment/comment-reply-list';
import { $CurrentArticleAsAvailable } from '@article/shared/atoms/current-article';
import { Avatar } from '~/components/avatar';
import { useAtomValue } from 'jotai';
import { CommentContent } from '../comment-item-content/index';
import cls from 'classnames';
import { CommentImage } from '../comment-item-image';
import { useActiveToCurrentArticle } from '@article/shared/utils/use-active-to-current-article';
import { ModuleType } from '~/utils/module-type';
import { ClickPosition } from '~/utils/click-position';
import useReflowClick from '~/utils/use-reflow-click';
import { $IsMobileUser } from '~/utils/is-mobile-user';

import './style.scss';

interface CommentItemProps {
  comment: TComment;
}

const CURRENT_PAGE_MODULE = ModuleType.comment;

export const CommentItem: FC<CommentItemProps> = (props) => {
  const { t } = useTranslation();
  const formatTimeAgo = useTimeAgoFormatter();
  const buildSemanticUrl = useSemanticUrlBuilder();
  const activeToCurrentArticle = useActiveToCurrentArticle();

  const { comment } = props;
  const isMobileUser = useAtomValue($IsMobileUser);
  const article = useAtomValue($CurrentArticleAsAvailable);
  const { author, createTime } = comment;
  const { avatar, nickName, linkName } = author;
  const isPrimaryComment = CommentUtil.isPrimaryComment(comment);
  const isReply = CommentUtil.isReply(comment);

  const userCanonicalUrl = useMemo(() => {
    return buildSemanticUrl(`/${linkName}`, article.articleRegion);
  }, [linkName]);

  const isAuthor = useMemo(() => {
    const articleAuthorId = article.author.userId;
    const commentAuthorId = comment.author.userId;
    return articleAuthorId === commentAuthorId;
  }, [article, comment]);

  const formattedCreatedTime = useMemo(() => formatTimeAgo(createTime), [formatTimeAgo, createTime]);

  const likeIconRef = useViewTracking<HTMLDivElement>({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: CURRENT_PAGE_MODULE,
        current_position: ClickPosition.comment_like,
      });
    }, []),
  });

  const commentAuthorObservedRef = useViewTracking<HTMLAnchorElement>({
    onView: useCallback(() => {
      if (isMobileUser) {
        window.Tea('wap_cta_show', {
          current_page_module: CURRENT_PAGE_MODULE,
          current_position: ClickPosition.comment_author,
        });
      }
    }, [isMobileUser]),
  });

  const handleClickUser = useReflowClick(
    {
      reflowBehavior: () => {
        activeToCurrentArticle(ClickPosition.comment_author);
      },
      clickEvent: () => ({
        current_position: ClickPosition.comment_author,
        page_click_id: comment.commentId,
      }),
    },
    [activeToCurrentArticle],
  );

  const commentReplyRef = useViewTracking<HTMLSpanElement>({
    onView: useCallback(() => {
      if (isMobileUser) {
        window.Tea('wap_cta_show', {
          current_page_module: CURRENT_PAGE_MODULE,
          current_position: ClickPosition.comment_reply,
        });
      }
    }, [isMobileUser]),
  });

  const handleCommentReplyClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToCurrentArticle(ClickPosition.comment_reply);
      },
      defaultBehavior: () => {
        activeToCurrentArticle(ClickPosition.comment_reply);
      },
      clickEvent: () => ({
        current_position: ClickPosition.comment_reply,
        page_click_id: comment.commentId,
      }),
    },
    [activeToCurrentArticle],
  );

  const handleClickLike = useReflowClick(
    {
      reflowBehavior: () => {
        activeToCurrentArticle(ClickPosition.comment_like);
      },
      defaultBehavior: () => {
        activeToCurrentArticle(ClickPosition.comment_like);
      },
      clickEvent: () => ({
        current_position: ClickPosition.comment_like,
        page_click_id: comment.commentId,
      }),
    },
    [activeToCurrentArticle],
  );

  return (
    <div className="article-comment-item-wrapper">
      <div className={cls('article-comment-item', { reply: isReply })}>
        <Link
          className="article-comment-item-avatar"
          to={userCanonicalUrl}
          onClick={handleClickUser}
          ref={commentAuthorObservedRef}
        >
          <Avatar src={avatar} alt={t('own_picture').replace('%s', nickName)} />
        </Link>

        <div className="article-comment-item-detail">
          <Link className="user" to={userCanonicalUrl}>
            <span>{nickName}</span>
            {isAuthor && <span className="tag-author">{t('author')}</span>}
          </Link>

          <div className="body">
            <CommentContent comment={comment} />
            <CommentImage comment={comment} />
          </div>

          <div className="footer">
            <div className="create-time">{formattedCreatedTime}</div>
            <span ref={commentReplyRef} className="reply" onClick={handleCommentReplyClick}>
              {t('seo_comment_reply')}
            </span>
          </div>
        </div>

        <div className="article-comment-item-action">
          <div className="likes-icon" ref={likeIconRef} onClick={handleClickLike}>
            <HeartIcon />
          </div>
        </div>
      </div>
      {
        // 一级评论渲染回复条数外露
        isPrimaryComment && Boolean(comment.replyCount) && <CommentReplyList comment={comment} />
      }
    </div>
  );
};
